import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  FileDownload as ExcelIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { supabase } from '../config/supabase';

export default function DataImportExport() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch contacts data
      const { data: contacts, error: contactsError } = await supabase
        .from('contacts')
        .select('*');
      
      if (contactsError) throw contactsError;

      // Fetch families data
      const { data: families, error: familiesError } = await supabase
        .from('families')
        .select('*');
      
      if (familiesError) throw familiesError;

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Create contacts worksheet
      const contactsWS = XLSX.utils.json_to_sheet(contacts);
      XLSX.utils.book_append_sheet(wb, contactsWS, 'Contacts');

      // Create families worksheet
      const familiesWS = XLSX.utils.json_to_sheet(families);
      XLSX.utils.book_append_sheet(wb, familiesWS, 'Families');

      // Save file
      XLSX.writeFile(wb, 'church_database_export.xlsx');
      setSuccess('Data exported successfully!');
    } catch (error) {
      setError('Error exporting data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImportCSV = async (event) => {
    try {
      setLoading(true);
      setError('');
      const file = event.target.files[0];
      
      if (!file) return;

      const reader = new FileReader();
      reader.onload = async ({ target }) => {
        const csv = Papa.parse(target.result, {
          header: true,
          skipEmptyLines: true
        });

        const { data } = csv;
        
        // Validate data structure
        if (!data || !data.length) {
          throw new Error('No valid data found in CSV file');
        }

        // Import contacts
        const { error } = await supabase
          .from('contacts')
          .insert(data);

        if (error) throw error;
        
        setSuccess('Data imported successfully!');
      };

      reader.readAsText(file);
    } catch (error) {
      setError('Error importing data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={() => setOpen(true)}
      >
        Import/Export Data
      </Button>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Data Import/Export</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
              </Box>
            )}
            <Typography variant="body1" gutterBottom>
              Import data from CSV file or export current data to Excel.
            </Typography>
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Import CSV
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={handleImportCSV}
                />
              </Button>
              <Button
                variant="contained"
                startIcon={<ExcelIcon />}
                onClick={handleExportExcel}
                disabled={loading}
              >
                Export to Excel
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
